import { HttpHeaders, HttpRequest } from '@angular/common/http';

export interface FetchObject {
  url: string;
  method: string;
  headers: { [key: string]: string };
  body?: string;
  credentials?: string;
}

export function fetchStringOrUrlToFetchObject(
  fetchStringOrUrl: string,
): FetchObject {
  const str = fetchStringOrUrl.trim();

  const isUrl = str.startsWith('http://') || str.startsWith('https://');
  const isRelativeUrl = str.startsWith('/');
  if (isUrl || isRelativeUrl) {
    return {
      url: str,
      method: 'GET',
      headers: {},
    };
  }

  const urlRegExp = /fetch\(["|'](((https?:\/\/)|(\/)).+?)["|']/;
  const url = urlRegExp.exec(str)?.[1];

  if (!url) {
    throw new Error(
      `No valid url found in fetch string: "${fetchStringOrUrl}".`,
    );
  }

  const locationOfFirstCurlyBracket = str.indexOf('{');
  const locationOfLastCurlyBracket = str.lastIndexOf('}');

  const fetchJsonString = str.substring(
    locationOfFirstCurlyBracket,
    locationOfLastCurlyBracket + 1,
  );

  const fetchObjectIncludingUnwantedProperties = JSON.parse(
    fetchJsonString,
  ) as Partial<FetchObject>;

  const method = fetchObjectIncludingUnwantedProperties.method?.toUpperCase();
  if (!method) {
    throw new Error(
      `No valid method found in fetch string: "${fetchStringOrUrl}".`,
    );
  }

  const headers = fetchObjectIncludingUnwantedProperties.headers ?? {};

  const fetchObject: FetchObject = {
    url,
    headers,
    method,
  };

  if (fetchObjectIncludingUnwantedProperties.body) {
    fetchObject.body = fetchObjectIncludingUnwantedProperties.body;
  }

  if (fetchObjectIncludingUnwantedProperties.credentials) {
    fetchObject.credentials =
      fetchObjectIncludingUnwantedProperties.credentials;
  }

  return fetchObject;
}

export function fetchObjectToHttpRequest(
  fetch: FetchObject,
): HttpRequest<unknown> {
  let withCredentials: boolean | undefined;
  if (fetch.credentials) {
    withCredentials = fetch.credentials === 'include';
  }

  const body = fetch.body ?? null;

  let headers: HttpHeaders | undefined;
  if (fetch.headers) {
    let httpHeaders = new HttpHeaders();
    Object.entries(fetch.headers).forEach(([key, value]) => {
      httpHeaders = httpHeaders.set(key, value);
    });
    headers = httpHeaders;
  }

  const contentTypeHeader = fetch.headers['content-type'];
  const responseType =
    contentTypeHeader && contentTypeHeader.includes('json') ? 'json' : 'text';

  const httpRequest = new HttpRequest(fetch.method, fetch.url, body, {
    headers,
    withCredentials,
    responseType,
  });

  return httpRequest;
}
